import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';
import { App as AntdApp } from 'antd';

/**
 * D4 (DISC-039 关闭): Admin 未保存守卫 —— 应用内任意导航 + 浏览器关闭/刷新 双层兜底。
 *
 * 装配前置: App.tsx 已切到 `createBrowserRouter` + `<RouterProvider>` (data router),
 * `useBlocker` 才可用 (在声明式 `<BrowserRouter>` 下会 invariant 抛错)。
 *
 * 守卫覆盖矩阵:
 * - ✅ 浏览器后退 / 前进键 (应用内 popstate): `useBlocker` 在导航前回调拦截, blocked
 *   态下 AntdApp.modal.confirm 显式询问, `proceed()` 放行 / `reset()` 留页。
 * - ✅ 左侧菜单点击 / 任意 `<Link>` / 任意 `navigate(...)`: 同样走 `useBlocker`,
 *   `AdminLayout.handleMenuClick` 不再重复挂 modal.confirm (整段已删, useBlocker 统一接管)。
 * - ✅ 刷新 / 关闭标签页 / 地址栏跳走外部 URL: `beforeunload` 不会被 useBlocker 覆盖
 *   (useBlocker 只拦应用内 router 导航), 仍单独挂监听; 两条防线互补不冲突
 *   (浏览器关闭时不会触发应用内导航, 不会同时弹两个)。
 *
 * useBlocker 拦截条件: `hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname`,
 * 避免同路径搜索参数变化触发误弹 modal。
 */
export const UNSAVED_GUARD_TEXT = {
  title: '当前页面有未保存修改',
  content: '离开页面会丢弃当前未保存内容。',
  okText: '放弃修改并离开',
  cancelText: '继续编辑',
} as const;

export function useUnsavedChangesGuard(hasUnsavedChanges: boolean): void {
  const { modal } = AntdApp.useApp();

  // 1) 应用内任意导航 (含浏览器后退/前进键 + 菜单 navigate() + 任意 <Link>):
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      return;
    }
    modal.confirm({
      title: UNSAVED_GUARD_TEXT.title,
      content: UNSAVED_GUARD_TEXT.content,
      okText: UNSAVED_GUARD_TEXT.okText,
      cancelText: UNSAVED_GUARD_TEXT.cancelText,
      onOk: () => blocker.proceed(),
      onCancel: () => blocker.reset(),
    });
  }, [blocker, modal]);

  // 2) 关页 / 刷新 / 地址栏外跳: useBlocker 不覆盖浏览器原生关闭, 仍单挂 beforeunload。
  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // 兼容旧浏览器: 必须设置 returnValue 才会弹原生确认。
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsavedChanges]);
}
